import React from 'react';
import { Brain, Shield, Mic, Phone, MessageSquare, HelpCircle, FileText, Link as LinkIcon, Users, Megaphone, BarChart3, Package, Repeat, CreditCard, Ticket, PhoneForwarded, PhoneCall, Headphones, ArrowLeftRight, UserCheck } from 'lucide-react';
import { features } from '../data/mock';

const iconMap = {
  brain: Brain,
  shield: Shield,
  mic: Mic,
  phone: Phone,
  message: MessageSquare,
  help: HelpCircle,
  file: FileText,
  link: LinkIcon,
  users: Users,
  megaphone: Megaphone,
  barChart: BarChart3,
  package: Package,
  repeat: Repeat,
  creditCard: CreditCard,
  ticket: Ticket,
  phoneForwarded: PhoneForwarded,
  phoneCall: PhoneCall,
  headphones: Headphones,
  transfer: ArrowLeftRight,
  invoice: FileText,
  team: UserCheck,
};

const categoryInfo = {
  core: { label: 'AI Agent Core', color: 'purple' },
  agent: { label: 'Central AI Agent', color: 'cyan' },
  communication: { label: 'Calls & Phone', color: 'teal' },
  sales: { label: 'Sales & CRM', color: 'blue' },
  invoicing: { label: 'Invoicing & Payments', color: 'emerald' },
  analytics: { label: 'Analytics & Support', color: 'amber' },
  management: { label: 'Management', color: 'rose' },
};

const colorClasses = {
  purple: { bg: 'from-purple-600 to-purple-500', badge: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  cyan: { bg: 'from-cyan-600 to-blue-500', badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  teal: { bg: 'from-teal-600 to-teal-500', badge: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  blue: { bg: 'from-blue-600 to-blue-500', badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  emerald: { bg: 'from-emerald-600 to-emerald-500', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  amber: { bg: 'from-amber-600 to-amber-500', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  rose: { bg: 'from-rose-600 to-rose-500', badge: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
};

const Features = () => {
  const categories = [...new Set(features.map(f => f.category))];

  return (
    <section id="features" className="py-20 px-6 bg-gradient-to-b from-transparent to-[#0a1628]" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Everything Your Business Needs
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            From AI call handling to live chat, invoicing, CRM, and sales outreach — Miadesk AI Agent is the all-in-one platform that runs your front office.
          </p>
        </div>

        {/* Category badges */}
        <div className="flex flex-wrap justify-center gap-2 mb-14">
          {categories.map((cat) => {
            const info = categoryInfo[cat];
            const colors = colorClasses[info.color];
            return (
              <span key={cat} className={`px-3 py-1 text-xs font-medium rounded-full border ${colors.badge}`} data-testid={`feature-category-${cat}`}>
                {info.label}
              </span>
            );
          })}
        </div>

        {/* Features grouped by category */}
        {categories.map((cat) => {
          const info = categoryInfo[cat];
          const colors = colorClasses[info.color];
          const catFeatures = features.filter(f => f.category === cat);
          return (
            <div key={cat} className="mb-12" data-testid={`feature-group-${cat}`}>
              <h3 className={`text-sm font-semibold uppercase tracking-wider mb-6 ${colors.badge.split(' ')[1]}`}>
                {info.label}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                {catFeatures.map((feature) => {
                  const Icon = iconMap[feature.icon] || Brain;
                  return (
                    <div key={feature.id} className="flex gap-4" data-testid={`feature-item-${feature.id}`}>
                      <div className="flex-shrink-0">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-white mb-1.5">
                          {feature.title}
                        </h4>
                        <p className="text-gray-400 leading-relaxed text-sm">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Features;
